import { Badge, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import itemsIconBlack from '@/assets/icons/items_black.svg'
import { TokenBase } from '@/schemas/types'

import {
  fetchMetadata,
  formatValueToSuffix,
  getMetadataLink,
  isValidURI,
  ItemMetadata,
  itemNameWithId,
  validateMetadata,
} from '../../utils/utils'
import RootButton from '../Buttons/RootButton'
import UserDetails from '../Details/UserDetails/UserDetails'
import { ImageCrop } from '../MediaWrappers/ImageCrop'
import FlagContentModal from '../Modals/Content/FlagContentModal'
import ModalSkeleton from '../Modals/ModalSkeleton'
import classes from './ItemsCard.module.css'
import MetadataNotFound from './MetadataNotFound'

export interface ItemsCardProps {
  elem: TokenBase
  setCount?: React.Dispatch<React.SetStateAction<string[]>>
  showCreator?: boolean
  parentRef?: React.RefObject<HTMLDivElement>
  isAdmin?: boolean
  triggerRefetch?: () => void
  isFlagged?: boolean
}

const ItemsCard: React.FC<ItemsCardProps> = ({
  elem,
  setCount = () => {},
  showCreator = true,
  parentRef,
  isAdmin = false,
  triggerRefetch = () => {},
  isFlagged = false,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const [error, setError] = useState<string>('')
  const [url, setUrl] = useState<string>('')
  const [isHovered, setIsHovered] = useState(false)
  const [opened, { open, close }] = useDisclosure(false)

  const defaultWidth = 294
  // if we want width to be defined by parent, we set it to 100%, otherwise we use defaultWidth
  const itemCardWidth = parentRef ? '100%' : defaultWidth
  const isFungible = useMemo(() => Number(elem.amount) > 1, [elem])

  const [nftMetadata, setNftMetadata] = useState<ItemMetadata | undefined>(undefined)
  useEffect(() => {
    if (!elem.metadata) {
      if (elem.uri) {
        if (isValidURI(elem.uri)) {
          fetchMetadata(elem.uri, elem.nativeId)
            .then(retval => setNftMetadata(retval))
            .catch(err => {
              setError(err.message)
              const metadataLink = getMetadataLink(elem.uri, elem.nativeId)
              // If the metadata link is not empty (not data://) we set it to the url
              if (metadataLink) {
                setUrl(metadataLink)
              }
            })
        } else {
          setError(t('components.items.invalidURI', 'Invalid URI'))
        }
      }
    } else {
      setNftMetadata(elem.metadata)
    }
  }, [elem])

  // Metadata validation
  const metadata: ItemMetadata | undefined = useMemo(
    () => validateMetadata(nftMetadata),
    [nftMetadata]
  )

  useEffect(() => {
    if (elem?.uri || nftMetadata) {
      setCount(count => {
        if (count.includes(elem.id)) {
          return count
        }
        return [...count, elem.id]
      })
    } else {
      setCount(count => count.filter(id => id !== elem.id))
      setError(t('components.items.errorLoadingMetadata', 'Metadata not found'))
    }
  }, [elem])

  const handleNavigation = useCallback(() => {
    if (elem && !error && !opened) {
      window.open(`/item/${elem.id}`, '_blank')
    }
  }, [elem, error, opened])

  const handleClose = useCallback(() => {
    close()
    triggerRefetch()
    setIsHovered(false)
  }, [close, triggerRefetch])

  return (
    <Stack
      style={{ gap: '15px', cursor: 'pointer', width: itemCardWidth, position: 'relative' }}
      ref={parentRef}
      onClick={handleNavigation}
      className={classes.card}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {!error ? (
        <>
          {isAdmin && isHovered && (
            <RootButton
              style1={false}
              error
              style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                zIndex: 1,
              }}
              onClick={e => {
                e.stopPropagation()
                open()
              }}
            >
              {isFlagged ? t('common.unflag', 'Unflag') : t('common.flag', 'Flag')}
            </RootButton>
          )}
          {metadata?.image && (
            <ImageCrop imageUrl={metadata.image} data={metadata?.cropInfo} parentRef={parentRef} />
          )}
          {(isHovered || isMobile) && (
            <Stack
              style={{
                width: itemCardWidth,
                position: 'absolute',
                top: '10px',
                alignItems: 'flex-end',
                right: '10px',
              }}
            >
              {isFungible ? (
                <Badge
                  color={theme.colors.barChartBlue[0]}
                  rightSection={
                    <img alt="" style={{ width: '11px', height: '11px' }} src={itemsIconBlack} />
                  }
                >
                  {formatValueToSuffix(Number(elem.amount), 4, 0)}
                </Badge>
              ) : (
                <Badge color={theme.colors.barChartYellow[0]}>Unique</Badge>
              )}
            </Stack>
          )}
          {metadata?.name && (
            <Stack style={{ width: itemCardWidth }} className={classes.blockText}>
              {showCreator && <UserDetails userId={elem.creator} hideCreatorName pfpSize="26px" />}
              <Stack
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: 5,
                  alignItems: 'center',
                }}
              >
                <Title
                  order={isMobile ? 5 : 4}
                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {itemNameWithId(metadata.name, elem.nativeId)}
                </Title>
                <Text style={{ color: theme.colors.paragraph[0], whiteSpace: 'nowrap' }}>
                  #{elem.nativeId.slice(-4)}
                </Text>
              </Stack>
            </Stack>
          )}
          {isAdmin && (
            <ModalSkeleton
              opened={opened}
              onClose={handleClose}
              title={
                isFlagged
                  ? t('components.modals.flagContent.unflagTitle', 'Unflag Content')
                  : t('components.modals.flagContent.flagTitle', 'Flag Content')
              }
            >
              <FlagContentModal
                close={handleClose}
                contentType="items"
                contentId={elem.id}
                isFlagged={isFlagged}
              />
            </ModalSkeleton>
          )}
        </>
      ) : (
        <MetadataNotFound error={error} url={url} elem={elem} parentRef={parentRef} />
      )}
    </Stack>
  )
}

export default ItemsCard
